import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Camera, Trash2, Loader2, User } from "lucide-react";
import { toast } from "sonner";

/**
 * Circular profile portrait uploader for candidates.
 * Fetches the portrait as an authenticated blob to avoid exposing tokens in URLs.
 */
export default function ProfilePhoto({ candidateUserId, editable = false, size = 96, onChange }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef();

    const load = async () => {
        if (!candidateUserId) return;
        setLoading(true);
        try {
            const resp = await api.get(`/candidates/${candidateUserId}/photo`, {
                responseType: "blob",
            });
            const blob = URL.createObjectURL(resp.data);
            setUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return blob;
            });
        } catch (_) {
            setUrl(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateUserId]);

    const trigger = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Photo trop volumineuse (max 3 Mo)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/candidates/photo", fd);
            toast.success("Photo mise à jour");
            await load();
            onChange && onChange();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setUploading(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Supprimer votre photo ?")) return;
        try {
            await api.delete("/candidates/photo");
            if (url) URL.revokeObjectURL(url);
            setUrl(null);
            toast.success("Photo supprimée");
            onChange && onChange();
        } catch (_) {
            toast.error("Erreur");
        }
    };

    const dim = { width: size, height: size };
    return (
        <div className="relative inline-block" data-testid="profile-photo">
            <div
                className="rounded-full overflow-hidden border-2 border-white shadow-[0_8px_24px_-8px_rgba(11,25,44,0.3)] bg-slate-100 flex items-center justify-center"
                style={dim}
            >
                {loading ? (
                    <Loader2 size={size / 3} className="animate-spin text-slate-400" />
                ) : url ? (
                    <img
                        src={url}
                        alt="Portrait"
                        className="w-full h-full object-cover"
                        style={dim}
                    />
                ) : (
                    <User size={size / 2.2} className="text-slate-400" />
                )}
            </div>
            {editable && (
                <>
                    <button
                        onClick={trigger}
                        disabled={uploading}
                        className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center shadow-lg hover:bg-[#f7b74a] transition-colors disabled:opacity-60"
                        title="Changer la photo"
                        data-testid="upload-photo-btn"
                    >
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                    </button>
                    {url && (
                        <button
                            onClick={remove}
                            className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-red-600 flex items-center justify-center"
                            title="Supprimer"
                            data-testid="remove-photo-btn"
                        >
                            <Trash2 size={12} />
                        </button>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onFile}
                        className="hidden"
                        data-testid="photo-file-input"
                    />
                </>
            )}
        </div>
    );
}
